import { emptyFurnaceAndThresholds } from "components/common/plan/emptyFurnaceAndThresholds";
import { useMemo } from "react";
import { HeatMixDetailsTab } from "components/strategyDashboard/heats/mixDetails/HeatMixDetailsTab";
import {
  ExternalRecipeWithSummary,
  HeatMix,
  MaterialRead,
  MixBasket,
  MixBasketLayer,
  MixMaterialBasketSummary,
  MixMaterialSummary,
  MixMassTotal,
  MixSummary,
  SummaryData,
  TotalledMix,
} from "src/store/api/generatedApi";
import { Period } from "hooks/periodIndex";

const SCRAP_CHEF_SOURCE = "scrapChef";

const buildTotalledMixFromSummary = (
  materialSummary: MixMaterialSummary[],
  basketSummary: MixMaterialBasketSummary[]
): TotalledMix => {
  const totals: MixMassTotal[] = materialSummary.map((ms) => ({
    material_name: ms.material,
    mass: ms.recipe_mass,
  }));

  const basketMap = new Map<number, MixBasketLayer[]>();
  for (const bs of basketSummary) {
    if (!basketMap.has(bs.basket_number)) {
      basketMap.set(bs.basket_number, []);
    }
    basketMap.get(bs.basket_number)?.push({
      material_name: bs.material,
      quantity: { value: bs.recipe_mass, unit: "mass" },
      layer_number: bs.basket_layer_number,
    });
  }

  const baskets: MixBasket[] = [...basketMap.entries()].map(
    ([basket_number, layers]) => ({ basket_number, layers })
  );

  return {
    ...emptyFurnaceAndThresholds(),
    baskets,
    totals,
    diffs: [],
    lifts: [],
    lift_diffs: [],
  };
};

const buildTotalledMixFromExternalRecipe = (
  externalRecipe: ExternalRecipeWithSummary
): TotalledMix => {
  const materialTotals = new Map<string, number>();
  for (const basket of externalRecipe.baskets) {
    for (const layer of basket.layers) {
      materialTotals.set(
        layer.material_name,
        (materialTotals.get(layer.material_name) ?? 0) + layer.quantity.value
      );
    }
  }

  const totals: MixMassTotal[] = [...materialTotals.entries()].map(
    ([material_name, mass]) => ({ material_name, mass })
  );

  return {
    ...emptyFurnaceAndThresholds(),
    baskets: externalRecipe.baskets,
    totals,
    diffs: [],
    lifts: [],
    lift_diffs: [],
  };
};

const unifyMaterials = (
  a: TotalledMix,
  b: TotalledMix
): { a: TotalledMix; b: TotalledMix } => {
  const aMaterials = new Set(a.totals.map((t) => t.material_name));
  const bMaterials = new Set(b.totals.map((t) => t.material_name));

  const missingFromA = [...bMaterials].filter((m) => !aMaterials.has(m));
  const missingFromB = [...aMaterials].filter((m) => !bMaterials.has(m));

  return {
    a: {
      ...a,
      totals: [
        ...a.totals,
        ...missingFromA.map((m) => ({ material_name: m, mass: 0 })),
      ],
    },
    b: {
      ...b,
      totals: [
        ...b.totals,
        ...missingFromB.map((m) => ({ material_name: m, mass: 0 })),
      ],
    },
  };
};

const buildExternalSummaryData = (
  externalRecipe: ExternalRecipeWithSummary
): SummaryData[] => {
  const s = externalRecipe.summary;
  return [
    {
      diff: null,
      name: "chargedPrice",
      value: s.cost_per_tonne_charged,
      unit_type: "specific_cost",
      decimal_places: 2,
    },
    {
      diff: null,
      name: "tappedPrice",
      value: s.cost_per_tonne_tapped,
      unit_type: "specific_cost",
      decimal_places: 2,
    },
    {
      diff: null,
      name: "chargedMass",
      value: s.recipe_mass,
      unit_type: "mass",
      decimal_places: 1,
    },
    {
      diff: null,
      name: "yield",
      value: s.yield_percent,
      unit_type: "yield",
      decimal_places: 1,
    },
  ];
};

const buildScrapChefSummaryData = (
  mixSummary: MixSummary,
  externalRecipe: ExternalRecipeWithSummary
): SummaryData[] => {
  const s = externalRecipe.summary;
  return [
    {
      name: "chargedPrice",
      value: mixSummary.cost_per_tonne_charged,
      unit_type: "specific_cost",
      decimal_places: 2,
      diff: mixSummary.cost_per_tonne_charged - s.cost_per_tonne_charged,
    },
    {
      name: "tappedPrice",
      value: mixSummary.cost_per_tonne_tapped,
      unit_type: "specific_cost",
      decimal_places: 2,
      diff: mixSummary.cost_per_tonne_tapped - s.cost_per_tonne_tapped,
    },
    {
      name: "chargedMass",
      value: mixSummary.recipe_mass,
      unit_type: "mass",
      decimal_places: 1,
      diff: mixSummary.recipe_mass - s.recipe_mass,
    },
    {
      name: "yield",
      value: mixSummary.yield_percent,
      unit_type: "yield",
      decimal_places: 1,
      diff: mixSummary.yield_percent - s.yield_percent,
    },
  ];
};

const computeDiffs = (
  targetTotals: MixMassTotal[],
  baseTotals: MixMassTotal[]
): MixMassTotal[] => {
  const baseByMaterial = new Map(
    baseTotals.map((t) => [t.material_name, t.mass])
  );
  return targetTotals.map((t) => ({
    material_name: t.material_name,
    mass: t.mass - (baseByMaterial.get(t.material_name) ?? 0),
  }));
};

export const RecipeComparisonTab = ({
  chemistryGroupName,
  period,
  mixNumber,
  mixMaterialSummary,
  mixMaterialBasketSummary,
  mixSummary,
  externalRecipe,
  materials,
}: {
  chemistryGroupName: string;
  period: Period;
  mixNumber: number;
  mixMaterialSummary: MixMaterialSummary[];
  mixMaterialBasketSummary: MixMaterialBasketSummary[];
  mixSummary: MixSummary;
  externalRecipe: ExternalRecipeWithSummary;
  materials: MaterialRead[];
}) => {
  const filteredMaterialSummary = useMemo(
    () =>
      mixMaterialSummary.filter(
        (ms) =>
          ms.chef_group_name === chemistryGroupName &&
          ms.period === period &&
          ms.mix_number === mixNumber
      ),
    [mixMaterialSummary, chemistryGroupName, period, mixNumber]
  );

  const filteredBasketSummary = useMemo(
    () =>
      mixMaterialBasketSummary.filter(
        (bs) =>
          bs.chef_group_name === chemistryGroupName &&
          bs.period === period &&
          bs.mix_number === mixNumber
      ),
    [mixMaterialBasketSummary, chemistryGroupName, period, mixNumber]
  );

  const mixes: HeatMix[] = useMemo(() => {
    const rawScrapChefMix = buildTotalledMixFromSummary(
      filteredMaterialSummary,
      filteredBasketSummary
    );
    const rawExternalMix = buildTotalledMixFromExternalRecipe(externalRecipe);

    const { a: scrapChefMix, b: externalRecipeMix } = unifyMaterials(
      rawScrapChefMix,
      rawExternalMix
    );

    return [
      {
        source: externalRecipe.source,
        ordering: 0,
        ignored_materials: [],
        mix: externalRecipeMix,
        plan_id: null,
        mix_id: null,
        total_material_cost: [],
        summary_data: buildExternalSummaryData(externalRecipe),
        user: null,
        is_equivalent_to_previous: false,
      },
      {
        source: SCRAP_CHEF_SOURCE,
        ordering: 1,
        ignored_materials: [],
        mix: {
          ...scrapChefMix,
          diffs: computeDiffs(scrapChefMix.totals, externalRecipeMix.totals),
        },
        plan_id: null,
        mix_id: null,
        total_material_cost: [],
        summary_data: buildScrapChefSummaryData(mixSummary, externalRecipe),
        user: null,
        is_equivalent_to_previous: false,
      },
    ];
  }, [
    filteredMaterialSummary,
    filteredBasketSummary,
    externalRecipe,
    mixSummary,
  ]);

  const hasBaskets =
    filteredBasketSummary.length > 0 || externalRecipe.baskets.length > 0;

  return (
    <HeatMixDetailsTab
      mixes={mixes}
      availableMixSources={[externalRecipe.source, SCRAP_CHEF_SOURCE]}
      ignoredMaterials={new Set()}
      hasLifts={false}
      hasBaskets={hasBaskets}
      materials={materials}
    />
  );
};
